import { useState } from 'react';
import { useForm } from 'react-hook-form';

type FormData = {
  name: string;
  email: string;
  phone: string;
  days: string[];
  dojo: string;
  dinner: string;
  sleepingPlace: string;
  message: string;
  terms: boolean;
};

const Seminars = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>();

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    setApiError(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL?.replace(/\/$/, '') || '';
      const response = await fetch(`${apiUrl}/api/seminar-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('❌ Server response:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error('Er ging iets mis bij het versturen van je aanmelding.');
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error(error);
      setApiError('Er is een fout opgetreden. Probeer het later opnieuw of neem contact op.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="pt-32 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <h1 className="text-3xl font-bold text-[#A91918] mb-4">Bedankt voor je aanmelding!</h1>
          <p className="text-lg text-gray-700 mb-6">
            We hebben je inschrijving ontvangen. Je ontvangt zo snel mogelijk een bevestiging per e-mail met de betalingsgegevens.
          </p>
          <p className="text-gray-600">
            Tot ziens op het seminar!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid md:grid-cols-3 gap-8 mb-12">
        {/* Left column - Content */}
        <div className="md:col-span-2">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-6">
              <h1 className="text-3xl font-bold text-[#A91918] mb-6">Patrick Cassidy, 7e dan komt 12-14 juni 2026 naar Arnhem!</h1>

              <div className="prose max-w-none">
                <h2 className="text-2xl font-semibold text-gray-800 mb-4">Schema:</h2>

                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-gray-700">Vrijdag - Anshin dojo (<a href="https://www.google.com/maps/place/Groot+Vijverdal,+Arnhem/@51.9927236,5.9230809,17z/data=!3m1!4b1!4m6!3m5!1s0x47c7a450edf39a0d:0x20270c38b99d0b9d!8m2!3d51.9927203!4d5.9256558!16s%2Fg%2F11h1ghnxr?entry=ttu&g_ep=EgoyMDI2MDEyMS4wIKXMDSoASAFQAw%3D%3D" target="_blank" rel="noopener noreferrer" className="text-[#A91918] hover:underline">Groot Vijverdal</a>)</h3>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>19:30 meditatie</li>
                    <li>20:00 - 22:00 aikido</li>
                  </ul>
                </div>

                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-gray-700">Zaterdag - Anshin dojo (<a href="https://www.google.com/maps/place/Groot+Vijverdal,+Arnhem/@51.9927236,5.9230809,17z/data=!3m1!4b1!4m6!3m5!1s0x47c7a450edf39a0d:0x20270c38b99d0b9d!8m2!3d51.9927203!4d5.9256558!16s%2Fg%2F11h1ghnxr?entry=ttu&g_ep=EgoyMDI2MDEyMS4wIKXMDSoASAFQAw%3D%3D" target="_blank" rel="noopener noreferrer" className="text-[#A91918] hover:underline">Groot Vijverdal</a>)</h3>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>11:00 meditatie</li>
                    <li>11:30 - 17:00 aikido</li>
                    <li>18:30 Restaurant 'Bloem'</li>
                  </ul>
                </div>

                <div className="mb-6">
                  <h3 className="text-xl font-semibold text-gray-700">Zondag - Valkenhuizen dojo (<a href="https://www.google.com/maps/place/Sportcentrum+Valkenhuizen/@52.0045318,5.926314,17z/data=!4m10!1m2!2m1!1sParkeerterrein+P1+Valkenhuizen!3m6!1s0x47c7a4f0b2a2d765:0xa97caf77edeedda8!8m2!3d52.0048301!4d5.9308507!15sCh5QYXJrZWVydGVycmVpbiBQMSBWYWxrZW5odWl6ZW5aICIecGFya2VlcnRlcnJlaW4gcDEgdmFsa2VuaHVpemVukgEOc3BvcnRzX2NvbXBsZXiaASRDaGREU1VoTk1HOW5TMFZKUTBGblNVTXlja3Q2VnpKblJSQULgAQD6AQQIABAt!16s%2Fg%2F11g6z2thwj?entry=ttu&g_ep=EgoyMDI2MDEyMS4wIKXMDSoASAFQAw%3D%3D" target="_blank" rel="noopener noreferrer" className="text-[#A91918] hover:underline">P1</a>)</h3>
                  <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>10:30 meditatie</li>
                    <li>11:00 - 14:00 aikido</li>
                    <li>14:00 Rokudan demonstratie</li>
                  </ul>
                </div>
              </div>

              <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-[#A91918] mb-3">Praktische informatie</h3>
                <ul className="space-y-2">
                  <li>
                    <span className="font-medium">Kosten:</span> €100 voor het hele weekend
                    <ul className="list-disc pl-5 mt-1 space-y-1 text-gray-600">
                      <li>Vrijdag €25</li>
                      <li>Zaterdag €60</li>
                      <li>Zondag €35</li>
                    </ul>
                  </li>
                  <li className="italic">10% vroegboekkorting als je voor 1 mei betaalt.</li>
                  <li className="mt-2"><span className="font-medium">Lunch:</span> breng voor jezelf en als je wilt iets mee om te delen. Thee en koffie aanwezig.</li>
                  <li><span className="font-medium">Meenemen:</span> meditatiekussen en wapens</li>
                </ul>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold text-[#A91918] mb-3">Betaling</h3>
                <p className="mb-2">IBAN: NL67 TRIO 0321038762 t.n.v. Aikido Vereniging Anshin</p>
                <p className="text-sm text-gray-600">Vermeld bij de betaling: Seminar Cassidy + je naam</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right column - Image and Form */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-1">
              <img
                src="/images/FlyerPatrick2026-2.jpg"
                alt="Patrick Cassidy Seminar 2026"
                className="w-full h-auto rounded-lg"
              />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-[#A91918] mb-4">Inschrijven</h2>
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">Naam *</label>
                  <input
                    type="text"
                    id="name"
                    {...register('name', { required: 'Dit veld is verplicht' })}
                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-[#A91918] focus:ring focus:ring-[#A91918] focus:ring-opacity-50"
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">E-mailadres *</label>
                  <input
                    type="email"
                    id="email"
                    {...register('email', {
                      required: 'Dit veld is verplicht',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Ongeldig e-mailadres'
                      }
                    })}
                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-[#A91918] focus:ring focus:ring-[#A91918] focus:ring-opacity-50"
                  />
                  {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>}
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">Telefoonnummer *</label>
                  <input
                    type="tel"
                    id="phone"
                    {...register('phone', { required: 'Dit veld is verplicht' })}
                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-[#A91918] focus:ring focus:ring-[#A91918] focus:ring-opacity-50"
                  />
                  {errors.phone && <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Registreer voor: *</label>
                  <div className="space-y-2">
                    {['Vrijdag', 'Zaterdag', 'Zondag'].map((day) => (
                      <label key={day} className="flex items-center">
                        <input
                          type="checkbox"
                          value={day}
                          {...register('days', { required: 'Kies minimaal één dag' })}
                          className="rounded border-gray-300 text-[#A91918] shadow-sm focus:border-[#A91918] focus:ring focus:ring-[#A91918] focus:ring-opacity-50"
                        />
                        <span className="ml-2 text-gray-700">{day}</span>
                      </label>
                    ))}
                  </div>
                  {errors.days && <p className="mt-1 text-sm text-red-600">{errors.days.message}</p>}
                </div>

                <div>
                  <label htmlFor="dojo" className="block text-sm font-medium text-gray-700">Dojo</label>
                  <input
                    type="text"
                    id="dojo"
                    {...register('dojo')}
                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-[#A91918] focus:ring focus:ring-[#A91918] focus:ring-opacity-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Eet je zaterdag mee in het restaurant? *</label>
                  <div className="flex gap-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        {...register('dinner', { required: 'Dit veld is verplicht' })}
                        value="yes"
                        className="form-radio text-[#A91918] focus:ring-[#A91918]"
                      />
                      <span className="ml-2">Ja</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        {...register('dinner', { required: 'Dit veld is verplicht' })}
                        value="no"
                        className="form-radio text-[#A91918] focus:ring-[#A91918]"
                      />
                      <span className="ml-2">Nee</span>
                    </label>
                  </div>
                  {errors.dinner && <p className="mt-1 text-sm text-red-600">{errors.dinner.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Heb je een slaapplaats nodig? *</label>
                  <div className="flex gap-4">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        {...register('sleepingPlace', { required: 'Dit veld is verplicht' })}
                        value="yes"
                        className="form-radio text-[#A91918] focus:ring-[#A91918]"
                      />
                      <span className="ml-2">Ja</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        {...register('sleepingPlace', { required: 'Dit veld is verplicht' })}
                        value="no"
                        className="form-radio text-[#A91918] focus:ring-[#A91918]"
                      />
                      <span className="ml-2">Nee</span>
                    </label>
                  </div>
                  {errors.sleepingPlace && <p className="mt-1 text-sm text-red-600">{errors.sleepingPlace.message}</p>}
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700">Bericht (optioneel)</label>
                  <textarea
                    id="message"
                    rows={3}
                    {...register('message')}
                    className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-[#A91918] focus:ring focus:ring-[#A91918] focus:ring-opacity-50"
                  />
                </div>

                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="terms"
                      type="checkbox"
                      {...register('terms', { required: 'Je moet akkoord gaan met de voorwaarden' })}
                      className="h-4 w-4 text-[#A91918] focus:ring-[#A91918] border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="terms" className="font-medium text-gray-700">
                      Ik ga akkoord met de verwerking van mijn gegevens volgens het privacybeleid *
                    </label>
                    {errors.terms && <p className="mt-1 text-sm text-red-600">{errors.terms.message}</p>}
                  </div>
                </div>

                <div>
                  {apiError && <p className="mb-4 text-sm text-red-600">{apiError}</p>}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#A91918] hover:bg-[#8a1414] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#A91918] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Bezig met versturen...' : 'Inschrijven'}
                  </button>
                </div>
              </form>
            </div>
          </div>


        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-12">
        <div className="md:flex">
          <div className="md:w-1/3">
            <img
              src="/images/seminar%20Patrick%20Alpen%202025.jpg"
              alt="Patrick Cassidy Seminar Alpen"
              className="w-full h-full object-cover"
              style={{ minHeight: '300px' }}
            />
          </div>
          <div className="p-8 md:w-2/3">
            <h3 className="text-2xl font-bold text-[#A91918] mb-4">Over Patrick Cassidy</h3>
            <p className="text-gray-700 leading-relaxed text-lg">
              Patrick beoefent al meer dan 40 jaar Aikido, Yoga en Meditatie en leidt de Evolutionary Aikido Community, waar Anshin onder valt. Hij heeft in Japan onder Saito Sensei in Iwama getraind, en heeft veel in Tibet, Nepal en India gereisd en gestudeerd. Hij heeft een grote dojo met uchi deshi programma in <a href="https://www.aikidomontreux.com/" target="_blank" rel="noopener noreferrer" className="text-[#A91918] hover:underline">Montreux, Zwitserland</a>. Zijn didactische stijl richt zich op de principes achter aikido om conflictoverstijgende vaardigheden en persoonlijke groei te stimuleren.
            </p>
          </div>
        </div>
      </div>
    </div >
  );
};

export default Seminars;
